<template>
  <div class="payout-detail-modal">
    <el-dialog :title="$t('data.payout_details')" :visible.sync="dialogTableVisible" class="diglog-card" width="780px">
      <div class="header-line" />
      <div class="total">
        <div class="total-item">
          <div class="total-title">{{ $t('data.paid') }}:</div>
          <div class="total-num"> {{ detailCurrency }} {{ merchantInfo.po_trans_amount | toDivideWithDot }}</div>
        </div>
        <div class="line" />
        <div class="total-item">
          <div class="total-title">{{ $t('data.fee') }}:</div>
          <div class="total-num">{{ detailCurrency }} {{ merchantInfo.fee_split | toDivideWithDot }}</div>
        </div>
      </div>

      <div class="section-title">
        {{ $t('data.timeline') }}
      </div>
      <div class="line-bottom-1px" />

      <div class="item">
        <span class="item-left content-blue">{{ $t('data.payout_created') }}</span>
        <span class="content-blue">{{ $t('data.payout_completed') }}</span>
      </div>

      <div class="item">
        <span class="item-left content-gray-time">{{ merchantInfo.transaction_create_time }}</span>
        <span class="content-gray-time">{{ merchantInfo.transaction_return_time }}</span>
      </div>

      <div class="section-title">
        {{ $t('data.description') }}
      </div>
      <div class="line-bottom-1px" />
      <div class="item">
        <span class="item-left content-blue">{{ $t('customerService.transaction_id') }}</span>
        <span class="content-gray">{{ merchantInfo.transaction_id }}</span>
      </div>
      <div class="item">
        <span class="item-left content-blue">{{ $t('customerService.merchant_order_id') }}</span>
        <span class="content-gray">{{ merchantInfo.merchant_order_id }}</span>
      </div>
      <div v-if="isDetailCountry('TH')" class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_account_type') }}</span>
        <span class="content-gray">{{ beneficiaryAccountTypeDetailFormat(merchantInfo.pay_method_type) }}</span>
      </div>
      <div v-if="isDetailCountry('TH') && merchantInfo.pay_method_type === 4" class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_type') }}</span>
        <span class="content-gray">{{ beneficiaryTypeDetailFormat(merchantInfo.account_type) }}</span>
      </div>
      <div class="item">
        <span class="item-left content-blue">{{ $t('customerService.remitting_bank') }}</span>
        <span class="content-gray">{{ merchantInfo.sender_bank_name }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('customerService.remittance_account') }}</span>
        <span class="content-gray">{{ merchantInfo.sender_bank_account | strAesDecryptAndAsterisk }}</span>
      </div>

      <div v-if="merchantInfo.disbursement_order" class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_id') }}</span>
        <span class="content-gray">{{ merchantInfo.disbursement_order }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_name') }}</span>
        <span class="content-gray">{{ merchantInfo.recipient_name | strAesDecryptAndAsterisk }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_channel') }}</span>
        <span class="content-gray">{{ merchantInfo.recipient_channel }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_account') }}</span>
        <span class="content-gray">{{ merchantInfo.recipient_account | strAesDecryptAndAsterisk }}</span>
      </div>

      <div class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_mobile') }}</span>
        <span class="content-gray">{{ merchantInfo.mobile | strAesDecryptAndAsterisk }}</span>
      </div>

      <!--受益人地址-->
      <div v-if="isDetailCountry('AE')" class="item">
        <span class="item-left content-blue">{{ $t('map.benefit_address') }}</span>
        <span class="content-gray">{{ merchantInfo.beneficiary_address }}</span>
      </div>

      <div v-if="merchantInfo.app_name" class="item">
        <span class="item-left content-blue">{{ $t('customerService.app_name') }}</span>
        <span class="content-gray">{{ merchantInfo.app_name }}</span>
      </div>

      <!--海湾六国收款人信息-->
      <template v-if="isGulfCountry">
        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_iban') }}</span>
          <span class="content-gray">{{ merchantInfo.iban }}</span>
        </div>

        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_swift_code') }}</span>
          <span class="content-gray">{{ merchantInfo.swift_code }}</span>
        </div>

        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_bank_address') }}</span>
          <span class="content-gray">{{ merchantInfo.bank_address }}</span>
        </div>

        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_en_first_name') }}</span>
          <span class="content-gray">{{ merchantInfo.en_first_name }}</span>
        </div>

        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_en_last_name') }}</span>
          <span class="content-gray">{{ merchantInfo.en_last_name }}</span>
        </div>

        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_native_first_name') }}</span>
          <span class="content-gray">{{ merchantInfo.local_first_name }}</span>
        </div>

        <div class="item">
          <span class="item-left content-blue">{{ $t('merchantAccount.beneficiary_native_last_name') }}</span>
          <span class="content-gray">{{ merchantInfo.local_last_name }}</span>
        </div>
      </template>

      <div class="item">
        <span class="item-left content-blue">{{ $t('merchantAccount.payment_remark') }}</span>
        <span class="content-gray" style="display: inline-flex;word-break: break-word; white-space: pre-line;">
          {{ merchantInfo.remark }}
        </span>
      </div>

      <div v-if="merchantInfo.po_trans_status === TRANSACTION_STATUS['fail']" class="item">
        <span class="item-left content-blue">{{ $t('data.fail_reason') }}</span>
        <span class="content-gray">{{ merchantInfo.fail_reason }}</span>
      </div>

      <el-tag :type="getPOTransactionStatusType(merchantInfo.po_trans_status)" min-width="150" class="payout-status tag-custom"> {{ getPOTransactionStatusLabel(merchantInfo.po_trans_status,merchantInfo.refund_type) }}</el-tag>
    </el-dialog>
  </div>
</template>

<script>
import { fetchPayoutDetail } from '@/api/customer-service/customer-service'
import { poTransStatusWithRefundMap } from '@/utils/maps'
import { GULF_USD_ONLY_COUNTRY_CODES } from '@/const/const.js'
export default {
  name: 'Po_Details_Modal',
  components: {
  },
  props: {
    detailId: {
      type: [Number, String],
      default: '0'
    }
  },
  data() {
    this.TRANSACTION_STATUS = {
      fail: 4
    }
    return {
      dialogTableVisible: false,
      listLoading: false,
      merchantInfo: { merchant: { merchant_basic_info: {}}, disbursement_fee: {}} // 用户基本信息
    }
  },
  computed: {
    detailCurrency() {
      return this.$store.getters.isAllCountry ? this.merchantInfo.transaction_currency : this.$store.getters.currency
    },
    beneficiaryAccountTypeDetailFormat() {
      const obj = {
        1: this.$t('map.beneficiary_account_type_0'),
        2: this.$t('map.beneficiary_account_type_0'),
        3: this.$t('map.beneficiary_account_type_0'),
        4: this.$t('map.beneficiary_account_type_1')
      }
      return function(value) {
        if (value) {
          return obj[value]
        }
        return value
      }
    },
    beneficiaryTypeDetailFormat() {
      const obj = {
        1: this.$t('map.beneficiary_type_1'),
        3: this.$t('map.beneficiary_type_0')
      }
      return function(value) {
        if (value) {
          return obj[value]
        }
        return value
      }
    },
    getPOTransactionStatusLabel() {
      return function(value, refund_type) {
        if (refund_type === 2) {
          value = 99
        }
        return poTransStatusWithRefundMap(value).label
      }
    },
    getPOTransactionStatusType() {
      return function(value) {
        return poTransStatusWithRefundMap(value).type
      }
    },
    remarkDescTxt() {
      let desc = this.merchantInfo.remark || ''
      desc = desc.replace(/(\r\n)/g, '<br/>')
      desc = desc.replace(/(\n)/g, '<br/>')
      return desc
    },
    isGulfCountry() {
      // 暂时只针对沙特
      return this.isDetailCountry('SA')
      // return GULF_USD_ONLY_COUNTRY_CODES.includes(this.$store.getters.curCountry.country_code)
    }
  },
  mounted() {
  },
  methods: {
    isDetailCountry(code) {
      const countryCode = this.$store.getters.isAllCountry
        ? this.merchantInfo.transaction_country
        : this.$store.getters.curCountry.country_code
      return countryCode === code
    },
    getDetailInfo(data) {
      fetchPayoutDetail({ transaction_id: data.disbursement_transaction_id }).then(res => {
        if (res.code === 10000) {
          this.merchantInfo = res.data
          // 兼容小额付款详情，取列表里面字段
          if (data.remark) {
            this.merchantInfo.remark = data.remark
          }
        }
      })
    },
    showModal(row) {
      this.dialogTableVisible = true
      if (row) {
        this.getDetailInfo(row)
      }
    },
    changModalStatus(value) {
      this.dialogTableVisible = value
    },

    onClickCancal() {
      this.changModalStatus(false)
    }
  }
}
</script>
<style>
.payout-detail-modal .el-dialog__title {
  font-weight: 500;  font-family: 'Inter-Medium';
  font-size: 16px;
  color: #000000;
}
.payout-detail-modal .header-line{
  margin: -26px 0 0;
  border-bottom: 1px solid #D3D7DF;
}
.diglog-card .el-dialog__title{
  padding-left: 0;
}
</style>
<style scoped lang="scss">
.total{
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 18px;
  min-width: 300px;

  .total-item{
    flex: 1;
    min-width: 0;
  }
  .line{
    display: inline-block;
    content:'';
    height: 52px;
    width: 1px;
    margin: 0 16px;
    background: #D3D7DF;
  }
  &-title{
    font-size: 14px;
    line-height: 17px;
    color: #606266;
  }
  &-num{
    margin-top: 12px;
    font-size: 22px;
    font-family: Source Han Sans SC;
    font-weight: 600;  font-family: 'Inter-SemiBold';
    line-height: 1.2;
    color: #3855F1;
    word-break: break-all;
  }
}
.item{
  display: flex;
  margin-top: 16px;
  .item-left{
    display: inline-block;
    width: 250px;
  }
}

.content-blue{
  font-weight: 500;  font-family: 'Inter-Medium';
  font-size: 14px;
  line-height: 17px;
  color: #1D3A71;
}
.content-gray{
  flex: 1;
  font-weight: 500;  font-family: 'Inter-Medium';
  font-size: 14px;
  line-height: 15px;
  color: #4B5567;
}
.content-gray-time{
  font-weight: 500;  font-family: 'Inter-Medium';
  font-size: 14px;
  line-height: 15px;
  color: #4B5567;
}

.payout-detail-modal{
  .payout-status{
    text-align: center;
    position: absolute;
    top: 13px;
    left: 160px;
  }
}
</style>
